import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useFetchErrorToast } from '@/hooks/use-fetch-error-toast';
import { timesheetsApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export interface RecentProductsResult {
  /** `true` once we have a settled response (success **or** error) for the
   *  current `(userId, window)` tuple — or there was no user to fetch for.
   *  Recents are a soft ordering hint, so a caller can render the picker
   *  before this settles; the band just fills in once it does. */
  isLoaded: boolean;
  /** Per-customer recently-used product ids, ordered most-recent-first.
   *  Customers the user hasn't logged against are absent (no empty entry) —
   *  callers should treat a missing key as "no recents." */
  byCustomer: ReadonlyMap<string, readonly string[]>;
}

const EMPTY: ReadonlyMap<string, readonly string[]> = new Map();

/**
 * Single batched query for the recently-used activities of every team customer
 * the timesheet owner (`userId`) is on over the displayed window — the "used
 * before by you" band of the activity picker. Mirrors
 * {@link import('./use-in-scope-products').useInScopeProductsByCustomer}: the
 * server derives the customer set from the user's memberships, so this sends
 * only a user + window, and the response is scoped + authorized to that user.
 * Failures surface as a toast and simply leave the Recent band empty — the
 * picker still works, it just loses the shortcut.
 */
export function useRecentProductsByCustomer(
  userId: string | undefined,
  fromDate: string,
  toDate: string,
): RecentProductsResult {
  // Same window guard as in-scope: an optimistic timer placeholder carries an
  // empty date sentinel until the server row swaps in, and the server 400s on
  // an empty fromDate/toDate. Skip until the dates are real.
  const enabled = Boolean(userId) && Boolean(fromDate) && Boolean(toDate);
  const query = useQuery({
    queryKey: queryKeys.timesheets.recentActivities({ userId: userId ?? '', fromDate, toDate }),
    queryFn: () => timesheetsApi.recentActivities({ userId, fromDate, toDate }),
    enabled,
    staleTime: 60_000,
  });

  useFetchErrorToast(query.error, 'recent activities');

  const byCustomer = useMemo(() => {
    const rows = query.data?.data ?? [];
    if (rows.length === 0) return EMPTY;
    const m = new Map<string, readonly string[]>();
    for (const entry of rows) m.set(entry.customerId, entry.productIds);
    return m;
  }, [query.data]);

  const isLoaded = !enabled || query.isFetched;

  return useMemo(() => ({ byCustomer, isLoaded }), [byCustomer, isLoaded]);
}
